import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

@Injectable({ providedIn: 'root' })
export class LanguageService {
  // Only languages we actually ship a translation file for. Adding 'fr'/'it'
  // here without the JSON would make those locales fall back key-by-key.
  private readonly supportedLangs = ['de', 'en'];
  private readonly defaultLang = 'de';
  private readonly storageKey = 'developer.lang';

  constructor(private readonly translate: TranslateService) {}

  /**
   * Initialises i18n once at startup. Applies a previously saved user preference,
   * otherwise the German default (same convention as Portal-SPA's LanguageService).
   */
  initLanguage(): void {
    this.translate.addLangs(this.supportedLangs);
    this.translate.setFallbackLang(this.defaultLang);

    document.documentElement.lang = this.resolveLang();
    this.translate.onLangChange.subscribe(e => { document.documentElement.lang = e.lang; });

    this.translate.use(this.resolveLang());
  }

  /**
   * Adopts the language of the account. The language is chosen in My-SPA and applies in every app, so
   * localStorage only caches it for the first paint after a reload. Unknown values are ignored.
   */
  applyServerLanguage(lang: string | null | undefined): void {
    if (!lang || !this.supportedLangs.includes(lang) || lang === this.currentLang) return;
    try { localStorage.setItem(this.storageKey, lang); } catch { /* storage unavailable */ }
    this.translate.use(lang);
  }

  /** The cached language, or null when this browser never stored one. */
  get savedLanguage(): string | null {
    let saved: string | null = null;
    try { saved = localStorage.getItem(this.storageKey); } catch { /* ignore */ }
    return saved && this.supportedLangs.includes(saved) ? saved : null;
  }

  get currentLang(): string {
    return this.translate.currentLang || this.defaultLang;
  }

  get languages(): string[] {
    return [...this.supportedLangs];
  }

  private resolveLang(): string {
    let saved: string | null = null;
    try { saved = localStorage.getItem(this.storageKey); } catch { /* ignore */ }
    return saved && this.supportedLangs.includes(saved) ? saved : this.defaultLang;
  }
}
